import React, { useMemo, useState } from "react";
import pluswhite from "assets/images/icon/plus-white.png";
import filtersearch from "assets/images/icon/filter-search.png";
import Table from "./Table";
import Input from "components/Input";
import AddEarningCodeModal from "pages/AddEarningCodeModal";
import EarningCodeDetailsModal from "pages/EarningCodeDetailsModal";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getEarningCodes, deleteEarningCode } from "services/earningCodes";

const headers = [
    { headerName: "Name", key: "name" },
    { headerName: "Earning Type", key: "earningType" },
    { headerName: "Active Status", key: "active_status" },
    { headerName: "", key: "action" },
];

const ManageEaringCodes = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selected, setSelected] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [defaultEditing, setDefaultEditing] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const queryClient = useQueryClient();

    const { data: codesRaw = [], isLoading } = useQuery({ queryKey: ['earning-codes'], queryFn: getEarningCodes });

    const deleteMutation = useMutation({
        mutationFn: (codeId) => deleteEarningCode(codeId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['earning-codes'] });
            setDeleteTarget(null);
        },
    });

    const tableData = useMemo(() => codesRaw
        .filter(c => !searchTerm || c.name?.toLowerCase().includes(searchTerm.toLowerCase()))
        .map(c => ({ id: c.id, name: c.name, earningType: c.type || '—', active_status: c.active ? 'Active' : 'Inactive' })),
        [codesRaw, searchTerm]);

    const handleEdit = (row) => {
        setSelected(codesRaw.find(c => c.id === row.id) || row);
        setDefaultEditing(true);
        setDetailsOpen(true);
    };

    const handleView = (row) => {
        setSelected(codesRaw.find(c => c.id === row.id) || row);
        setDefaultEditing(false);
        setDetailsOpen(true);
    };

    const handleDelete = (row) => {
        setDeleteTarget(codesRaw.find(c => c.id === row.id) || row);
    };

    return (
        <>
            <div>
                <div className='d-flex justify-content-between align-content-end border-bottom pb-4'>
                    <div>
                        <h2 className='sub-title'>Manage Earning Codes</h2>
                        <button className='button-orange py-2 mt-3 rounded-pill d-flex justify-content-center align-items-center gap-2' onClick={() => setIsOpen(true)}>
                            <img src={pluswhite} alt="add" />Add Earning Code
                        </button>
                    </div>
                    <div className='align-content-end'>
                        <Input placeholder='Search by Name' startIcon={filtersearch} style={{ width: '300px' }} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                    </div>
                </div>
                <div>
                    {isLoading ? <p className="text-center mt-4">Loading earning codes...</p> : (
                        <Table headers={headers} data={tableData} showPagination={true} handleEdit={handleEdit} handleView={handleView} handleDelete={handleDelete} />
                    )}
                </div>
            </div>

            {/* Delete confirmation modal */}
            {deleteTarget && (
                <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
                        <div className="modal-content p-4">
                            <h5 className="mb-2">Delete Earning Code</h5>
                            <p className="text-muted mb-4">Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This action cannot be undone.</p>
                            {deleteMutation.isError && <p className="text-danger mb-3">Failed to delete. Please try again.</p>}
                            <div className="d-flex gap-2 justify-content-end">
                                <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>Cancel</button>
                                <button
                                    className="btn btn-danger rounded-pill px-4"
                                    onClick={() => deleteMutation.mutate(deleteTarget.id)}
                                    disabled={deleteMutation.isPending}
                                >
                                    {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <AddEarningCodeModal open={isOpen} onClose={() => { setIsOpen(false); queryClient.invalidateQueries({ queryKey: ['earning-codes'] }); }} />
            <EarningCodeDetailsModal
                code={selected}
                open={detailsOpen}
                defaultEditing={defaultEditing}
                onClose={() => { setDetailsOpen(false); setSelected(null); queryClient.invalidateQueries({ queryKey: ['earning-codes'] }); }}
            />
        </>
    );
};

export default ManageEaringCodes;
